import React, { useMemo, useState } from 'react';
import '../styles/Checkout.css';
import { useCart } from '../context/CartContext';
import { postJson } from '../api';

const initialState = {
  name: '',
  email: '',
  address: '',
  city: '',
  state: '',
  zip: '',
};

function Checkout() {
  const { items, total, clearCart } = useCart();
  const [formData, setFormData] = useState(initialState);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const orderTotal = useMemo(() => total.toFixed(2), [total]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: '', message: '' });

    if (items.length === 0) {
      setStatus({ type: 'error', message: 'Add items to your cart before checking out.' });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await postJson('/checkout', {
        ...formData,
        items,
      });
      setStatus({
        type: 'success',
        message: `Order ${response.orderNumber} placed successfully.`,
      });
      setFormData(initialState);
      clearCart();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>Checkout</h2>
      <section className="cart-items" aria-live="polite">
        <strong>Cart Summary</strong>
        {items.length === 0 ? (
          <p>Your cart is empty.</p>
        ) : (
          <>
            {items.map((item) => (
              <div key={item.id} className="cart-item">
                <span>
                  {item.name} &times; {item.quantity}
                </span>
                <span>${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="cart-total">
              <span>Total</span>
              <span>${orderTotal}</span>
            </div>
          </>
        )}
      </section>
      <form className="checkout-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="checkout-email">Email:</label>
          <input
            id="checkout-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="address">Shipping Address:</label>
          <input
            id="address"
            name="address"
            type="text"
            value={formData.address}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="city">City:</label>
          <input
            id="city"
            name="city"
            type="text"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="state">State:</label>
          <input
            id="state"
            name="state"
            type="text"
            value={formData.state}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="zip">Zip Code:</label>
          <input
            id="zip"
            name="zip"
            type="text"
            value={formData.zip}
            onChange={handleChange}
            pattern="\\d{5}"
            inputMode="numeric"
            required
          />
        </div>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Placing Order...' : 'Place Order'}
        </button>
      </form>
      {status.message && (
        <div
          role="status"
          aria-live="polite"
          className={`status-message ${status.type}`}
        >
          {status.message}
        </div>
      )}
    </div>
  );
}

export default Checkout;
